/**
 * Projections into the editor's single lifecycle vocabulary — one from the editor's own signals, one
 * from the lifecycle machine's phase.
 *
 * Nothing here reads or mutates runtime state, and nothing here is authoritative: callers pass what
 * they already know and get back the phase it describes. Keeping both projections in one module is
 * what guarantees the two sides share a vocabulary, so comparing them introduces no second
 * normalization of editor state.
 */
import type { RemixPhase } from '../../state/types';

import type { EditorRemixLifecyclePhase } from './types';

export type LegacyEditorRemixSignals = {
	/** The editor is waiting on generation — the thinking bar or decoration is live. */
	isThinking: boolean;
	/** Generation produced a previewable result. */
	isReady: boolean;
	/** The staging-area preview is on screen. */
	isPreviewVisible: boolean;
	/** Insertion into the document has begun. */
	isInserting: boolean;
	/** Generation failed, including safety and AUP violations. */
	isError: boolean;
	/** Generation was stopped from outside the editor, e.g. the Rovo side chat. */
	isAborted: boolean;
	/** The user dismissed or cancelled the flow from an editor surface. */
	isCancelled: boolean;
};

/**
 * Derives the editor's lifecycle phase from the editor's own signals.
 *
 * The result is reported to the product's lifecycle adapter for migration shadow comparison, so
 * this must stay a pure projection of existing signals rather than a second source of truth.
 *
 * Precedence mirrors the renderer's equivalent projection so both surfaces resolve an ambiguous
 * moment the same way: a terminal outcome wins over any in-flight phase, and a later phase wins
 * over an earlier one it supersedes.
 */
export const deriveLegacyEditorRemixPhase = ({
	isThinking,
	isReady,
	isPreviewVisible,
	isInserting,
	isError,
	isAborted,
	isCancelled,
}: LegacyEditorRemixSignals): EditorRemixLifecyclePhase => {
	if (isError) {
		return 'error';
	}

	if (isAborted || isCancelled) {
		return 'aborted';
	}

	// Insertion outranks the preview because the preview stays mounted while content is written
	// into the document, so both signals are true for the whole of insertion.
	if (isInserting) {
		return 'inserting';
	}

	if (isPreviewVisible) {
		return 'previewing';
	}

	if (isReady) {
		return 'ready';
	}

	if (isThinking) {
		return 'thinking';
	}

	return 'inactive';
};

/**
 * The machine's phases map one-to-one onto the editor's vocabulary. Unlike the renderer, the editor
 * has a real `inserting` phase of its own, so nothing is relabeled here.
 */
const MACHINE_TO_EDITOR_PHASE = {
	thinking: 'thinking',
	ready: 'ready',
	previewing: 'previewing',
	inserting: 'inserting',
	error: 'error',
	aborted: 'aborted',
} as const satisfies Record<RemixPhase, EditorRemixLifecyclePhase>;

/**
 * Projects a machine phase into the editor's vocabulary.
 *
 * An absent phase is `inactive`, which covers both "no flow has been admitted yet" and "the flow's
 * machine has already been destroyed". Callers that need to survive that teardown must capture the
 * phase before releasing machine ownership rather than re-reading it afterwards.
 *
 * An unmapped phase is also `inactive`. The machine's `state()` is typed `RemixPhase`, but its
 * actual state set is wider — `done` is a real state that `RemixPhase` omits — so a phase outside
 * the map is reachable at runtime even though the type says it is not. No caller can observe `done`
 * today, because the flow controller destroys the machine on reaching it, but returning `undefined`
 * through a signature that forbids it would be a silent lie if that ever changed.
 */
export const deriveMachineEditorRemixPhase = (
	phase: RemixPhase | undefined,
): EditorRemixLifecyclePhase => {
	if (phase === undefined) {
		return 'inactive';
	}

	// Cast widens the key type only; `satisfies` above still requires every `RemixPhase` to be
	// mapped, so this cannot mask a genuinely missing entry for a declared phase.
	const mapped = (MACHINE_TO_EDITOR_PHASE as Record<string, EditorRemixLifecyclePhase | undefined>)[
		phase
	];
	return mapped ?? 'inactive';
};

/** A phase from which no further lifecycle transition is expected. */
export type EditorRemixTerminalLifecyclePhase = Extract<
	EditorRemixLifecyclePhase,
	'error' | 'aborted'
>;

export const isTerminalEditorRemixPhase = (
	phase: EditorRemixLifecyclePhase,
): phase is EditorRemixTerminalLifecyclePhase => phase === 'error' || phase === 'aborted';
